// src/api/firebase/firestore.ts
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  serverTimestamp,
  DocumentData,
  Query,
  CollectionReference,
  DocumentReference,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "./config";

// Collection references with proper typing
export const usersCollection = collection(
  db,
  "users",
) as CollectionReference<DocumentData>;
export const activitiesCollection = collection(
  db,
  "activities",
) as CollectionReference<DocumentData>;

// Types for pagination
interface PaginationOptions {
  field: string;
  direction: "asc" | "desc";
}

interface PaginatedResult<T> {
  data: T[];
  total: number;
  totalPages: number;
  currentPage: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// CRUD Operations with proper typing
export const firestoreService = {
  // Generic get all documents
  getAll: async <T>(
    collectionRef: CollectionReference<DocumentData>,
  ): Promise<T[]> => {
    const snapshot = await getDocs(collectionRef);
    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
      } as T;
    });
  },

  // Get paginated documents
  getPaginated: async <T>(
    collectionRef: CollectionReference<DocumentData>,
    page: number = 1,
    pageSize: number = 10,
    orderByOptions: PaginationOptions = {
      field: "created_at",
      direction: "desc",
    },
  ): Promise<PaginatedResult<T>> => {
    try {
      // Get total count
      const totalSnapshot = await getCountFromServer(collectionRef);
      const total = totalSnapshot.data().count;
      const totalPages = Math.ceil(total / pageSize);

      // Build base query
      let q = query(
        collectionRef,
        orderBy(orderByOptions.field, orderByOptions.direction),
        limit(pageSize),
      );

      // If not first page, get the last document from previous page
      if (page > 1) {
        const previousPageQuery = query(
          collectionRef,
          orderBy(orderByOptions.field, orderByOptions.direction),
          limit((page - 1) * pageSize),
        );
        const previousPageSnapshot = await getDocs(previousPageQuery);
        const lastVisible =
          previousPageSnapshot.docs[previousPageSnapshot.docs.length - 1];

        if (lastVisible) {
          q = query(
            collectionRef,
            orderBy(orderByOptions.field, orderByOptions.direction),
            startAfter(lastVisible),
            limit(pageSize),
          );
        }
      }

      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as T[];

      return {
        data,
        total,
        totalPages,
        currentPage: page,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      };
    } catch (error) {
      console.error("Error getting paginated data:", error);
      throw error;
    }
  },

  // Search with pagination
  searchPaginated: async <T>(
    collectionRef: CollectionReference<DocumentData>,
    searchTerm: string,
    page: number = 1,
    pageSize: number = 10,
    searchFields: string[] = ["name", "email", "username"],
  ): Promise<PaginatedResult<T>> => {
    try {
      if (!searchTerm || searchTerm.length < 3) {
        return {
          data: [],
          total: 0,
          totalPages: 0,
          currentPage: page,
          hasNext: false,
          hasPrev: false,
        };
      }

      // Create queries for each search field
      const queries = searchFields.map((field) =>
        query(
          collectionRef,
          where(field, ">=", searchTerm),
          where(field, "<=", searchTerm + "\uf8ff"),
          orderBy(field),
          limit(pageSize * 3), // Get more results to combine and paginate
        ),
      );

      // Execute all queries
      const snapshots = await Promise.all(queries.map((q) => getDocs(q)));

      // Combine results and remove duplicates
      const dataMap = new Map();
      snapshots.forEach((snapshot) => {
        snapshot.docs.forEach((doc) => {
          if (!dataMap.has(doc.id)) {
            dataMap.set(doc.id, {
              id: doc.id,
              ...doc.data(),
            });
          }
        });
      });

      let allData = Array.from(dataMap.values()) as T[];
      const total = allData.length;
      const totalPages = Math.ceil(total / pageSize);

      // Apply pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = allData.slice(startIndex, endIndex);

      return {
        data: paginatedData,
        total,
        totalPages,
        currentPage: page,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      };
    } catch (error) {
      console.error("Error searching paginated data:", error);
      throw error;
    }
  },

  // Get single document
  getById: async <T>(
    collectionRef: CollectionReference<DocumentData>,
    id: string,
  ): Promise<T | null> => {
    const docRef = doc(collectionRef, id) as DocumentReference<DocumentData>;
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;

    const data = snapshot.data();
    return {
      id: snapshot.id,
      ...data,
    } as T;
  },

  // Create document
  create: async <T extends DocumentData>(
    collectionRef: CollectionReference<DocumentData>,
    data: Omit<T, "id">,
  ): Promise<string> => {
    const docRef = await addDoc(collectionRef, {
      ...data,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    });
    return docRef.id;
  },

  // Update document
  update: async <T extends DocumentData>(
    collectionRef: CollectionReference<DocumentData>,
    id: string,
    data: Partial<T>,
  ): Promise<void> => {
    const docRef = doc(collectionRef, id);
    await updateDoc(docRef, {
      ...data,
      updated_at: serverTimestamp(),
    });
  },

  // Delete document
  delete: async (
    collectionRef: CollectionReference<DocumentData>,
    id: string,
  ): Promise<void> => {
    const docRef = doc(collectionRef, id);
    await deleteDoc(docRef);
  },

  // Query documents with pagination support
  query: async <T>(
    collectionRef: CollectionReference<DocumentData>,
    conditions: { field: string; operator: any; value: any }[] = [],
    sortField?: string,
    sortOrder: "asc" | "desc" = "desc",
    docLimit?: number,
    startAfterDoc?: QueryDocumentSnapshot<DocumentData>,
  ): Promise<T[]> => {
    let q: Query<DocumentData> = query(collectionRef);

    // Apply where conditions
    conditions.forEach((condition) => {
      q = query(q, where(condition.field, condition.operator, condition.value));
    });

    // Apply sorting
    if (sortField) {
      q = query(q, orderBy(sortField, sortOrder));
    }

    // Apply startAfter for pagination
    if (startAfterDoc) {
      q = query(q, startAfter(startAfterDoc));
    }

    // Apply limit
    if (docLimit) {
      q = query(q, limit(docLimit));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
      } as T;
    });
  },

  // Get last document for pagination
  getLastDocument: async <T>(
    collectionRef: CollectionReference<DocumentData>,
    sortField: string = "created_at",
    sortOrder: "asc" | "desc" = "desc",
  ): Promise<QueryDocumentSnapshot<DocumentData> | null> => {
    const q = query(collectionRef, orderBy(sortField, sortOrder), limit(1));
    const snapshot = await getDocs(q);
    return snapshot.docs[0] || null;
  },
};
